import React, { useState, lazy, Suspense } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_IMAGES, COMPARE_IMAGES } from '../graphql/queries';
import OsIcon from '../components/OsIcon';

const DiffEditor = lazy(() => import('@monaco-editor/react').then((mod) => ({ default: mod.DiffEditor })));

function severityColor(severity) {
  const map = { CRITICAL: '#d63031', HIGH: '#e17055', MEDIUM: '#fdcb6e', LOW: '#00b894', INFORMATIONAL: '#636e72' };
  return map[severity] || '#636e72';
}

function actionLabel(action) {
  const map = { added: 'Added', removed: 'Removed', upgraded: 'Upgraded', downgraded: 'Downgraded' };
  return map[action] || action;
}

function actionClass(action) {
  const map = { added: 'diff-added', removed: 'diff-removed', upgraded: 'diff-added', downgraded: 'diff-removed' };
  return map[action] || 'diff-unchanged';
}

function packagesToDiffText(packages, side) {
  if (!packages || packages.length === 0) return '';
  return packages
    .map((pkg) => `${pkg.packageName}: ${side === 'a' ? (pkg.versionA || '(not installed)') : (pkg.versionB || '(not installed)')}`)
    .join('\n');
}

export default function ImageComparison() {
  const { data: imagesData, loading: imagesLoading, error: imagesError } = useQuery(GET_IMAGES, { variables: { limit: 100 } });
  const [compareImages, { data: compareData, loading: comparing, error: compareError }] = useLazyQuery(COMPARE_IMAGES);
  const [imageIdA, setImageIdA] = useState('');
  const [imageIdB, setImageIdB] = useState('');
  const [showDiffEditor, setShowDiffEditor] = useState(false);

  const images = imagesData?.listImages?.items || [];

  const handleCompare = () => {
    if (!imageIdA || !imageIdB) return;
    compareImages({ variables: { imageIdA, imageIdB } });
  };

  const result = compareData?.compareImages;

  return (
    <div>
      <h2 className="page-title">Image Comparison</h2>

      {imagesError && <div className="error">Failed to load images: {imagesError.message}</div>}

      <div className="card">
        <h3>Select Images to Compare</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Image A</label>
            <select value={imageIdA} onChange={(e) => setImageIdA(e.target.value)} disabled={imagesLoading}>
              <option value="">Select an image...</option>
              {images.map((img) => (
                <option key={img.id} value={img.id}>{img.name} ({img.blueprintName})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Image B</label>
            <select value={imageIdB} onChange={(e) => setImageIdB(e.target.value)} disabled={imagesLoading}>
              <option value="">Select an image...</option>
              {images.map((img) => (
                <option key={img.id} value={img.id}>{img.name} ({img.blueprintName})</option>
              ))}
            </select>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleCompare} disabled={!imageIdA || !imageIdB || comparing}>
          {comparing ? 'Comparing...' : 'Compare Images'}
        </button>
      </div>

      {compareError && <div className="error">Failed to compare images: {compareError.message}</div>}

      {result && (
        <>
          <div className="comparison-container">
            <div className="comparison-side">
              <div className="card">
                <h3><Link to={`/images/${result.imageA.id}`} className="link">Image A</Link></h3>
                <div className="detail-grid">
                  <div className="detail-item"><label>Name</label><p>{result.imageA.name}</p></div>
                  <div className="detail-item"><label>Blueprint</label><p><OsIcon name={result.imageA.blueprintName} showLabel /></p></div>
                  <div className="detail-item"><label>Version</label><p>{result.imageA.blueprintVersion}</p></div>
                  <div className="detail-item"><label>Status</label><p>{result.imageA.status}</p></div>
                  <div className="detail-item"><label>Created</label><p>{result.imageA.createdAt ? new Date(result.imageA.createdAt).toLocaleDateString() : '-'}</p></div>
                </div>
              </div>
            </div>
            <div className="comparison-side">
              <div className="card">
                <h3><Link to={`/images/${result.imageB.id}`} className="link">Image B</Link></h3>
                <div className="detail-grid">
                  <div className="detail-item"><label>Name</label><p>{result.imageB.name}</p></div>
                  <div className="detail-item"><label>Blueprint</label><p><OsIcon name={result.imageB.blueprintName} showLabel /></p></div>
                  <div className="detail-item"><label>Version</label><p>{result.imageB.blueprintVersion}</p></div>
                  <div className="detail-item"><label>Status</label><p>{result.imageB.status}</p></div>
                  <div className="detail-item"><label>Created</label><p>{result.imageB.createdAt ? new Date(result.imageB.createdAt).toLocaleDateString() : '-'}</p></div>
                </div>
              </div>
            </div>
          </div>

          {result.benchmarkDiff && (
            <div className="card">
              <h3>Benchmark Score Comparison</h3>
              <div className="comparison-container">
                <div className="comparison-side" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 36, fontWeight: 700 }}>{result.benchmarkDiff.scoreA}</div>
                  <div style={{ color: '#636e72', fontSize: 13 }}>Image A Score</div>
                </div>
                <div className="comparison-side" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 36, fontWeight: 700 }}>{result.benchmarkDiff.scoreB}</div>
                  <div style={{ color: '#636e72', fontSize: 13 }}>Image B Score</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 16 }}>
                <div className="diff-added" style={{ padding: '8px 16px', borderRadius: 6 }}>
                  +{result.benchmarkDiff.newPasses?.length || 0} new passes
                </div>
                <div className="diff-removed" style={{ padding: '8px 16px', borderRadius: 6 }}>
                  +{result.benchmarkDiff.newFailures?.length || 0} new failures
                </div>
              </div>
            </div>
          )}

          {result.cvesDiff && (
            <div className="card">
              <h3>CVE Differences</h3>
              <p style={{ color: '#636e72', fontSize: 13, marginBottom: 12 }}>
                {result.cvesDiff.unchangedCount || 0} unchanged CVEs
              </p>

              {result.cvesDiff.addedCves?.length > 0 && (
                <>
                  <h4 style={{ marginTop: 16, marginBottom: 8 }}>Added CVEs (in Image B but not A)</h4>
                  <table>
                    <thead>
                      <tr><th>CVE ID</th><th>Severity</th><th>Title</th></tr>
                    </thead>
                    <tbody>
                      {result.cvesDiff.addedCves.map((cve, i) => (
                        <tr key={i} className="diff-added">
                          <td>{cve.cveId}</td>
                          <td>
                            <span className="badge" style={{ background: severityColor(cve.severity) + '22', color: severityColor(cve.severity) }}>
                              {cve.severity}
                            </span>
                          </td>
                          <td>{cve.title || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {result.cvesDiff.removedCves?.length > 0 && (
                <>
                  <h4 style={{ marginTop: 16, marginBottom: 8 }}>Removed CVEs (in Image A but not B)</h4>
                  <table>
                    <thead>
                      <tr><th>CVE ID</th><th>Severity</th><th>Title</th></tr>
                    </thead>
                    <tbody>
                      {result.cvesDiff.removedCves.map((cve, i) => (
                        <tr key={i} className="diff-removed">
                          <td>{cve.cveId}</td>
                          <td>
                            <span className="badge" style={{ background: severityColor(cve.severity) + '22', color: severityColor(cve.severity) }}>
                              {cve.severity}
                            </span>
                          </td>
                          <td>{cve.title || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {!result.benchmarkDiff && !result.cvesDiff?.addedCves?.length && !result.cvesDiff?.removedCves?.length && !result.packageDiff?.length && (
            <div className="card">
              <p style={{ color: '#636e72', textAlign: 'center' }}>No differences found between these images.</p>
            </div>
          )}

          {result.packageDiff?.length > 0 && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3>Package Differences</h3>
                <button
                  className="btn"
                  style={{ padding: '4px 12px', fontSize: 12, background: '#dfe6e9' }}
                  onClick={() => setShowDiffEditor(!showDiffEditor)}
                >
                  {showDiffEditor ? 'Table View' : 'Diff View'}
                </button>
              </div>

              {showDiffEditor ? (
                <Suspense fallback={<div className="loading">Loading diff editor...</div>}>
                  <DiffEditor
                    height={400}
                    language="plaintext"
                    original={packagesToDiffText(result.packageDiff, 'a')}
                    modified={packagesToDiffText(result.packageDiff, 'b')}
                    options={{ readOnly: true, renderSideBySide: true, minimap: { enabled: false } }}
                    theme="vs-dark"
                  />
                </Suspense>
              ) : (
                <table>
                  <thead>
                    <tr><th>Package</th><th>Version A</th><th>Version B</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {result.packageDiff.map((pkg, i) => (
                      <tr key={i} className={actionClass(pkg.action)}>
                        <td>{pkg.packageName}</td>
                        <td>{pkg.versionA || '-'}</td>
                        <td>{pkg.versionB || '-'}</td>
                        <td>
                          <span className={`badge ${actionClass(pkg.action)}`}>
                            {actionLabel(pkg.action)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
